import React, { FunctionComponent, useEffect, useMemo } from 'react';
import { observer } from 'mobx-react';
import Dialog from '~/component/dialog';
import strings from '~/constants/strings';
import medicalCareStore from '~/store/medicalCareStore';
import { CloseCircleOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { styled } from '@mui/material/styles';
import { Text } from '~/component/text';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { SpecialAvailability, TempHourAvailability } from '../types';
import accountStore from '~/store/accountStore';
import {
  getAvailabilityByDate,
  getFinalSpecialDays,
} from '../HoursView/helpers';
import { Button, Grid2 } from '@mui/material';
import { TimeSelectRow } from './TimeSelectRow';
import { checkForOverlapping } from './helpers';

const maxHeight = 422;
const baseCalendarRowCount = 5;
const calendarRowHeight = 36;
const calendarHeaderHeight = 58;

const getCalendarRowCountByMonth = (month: Dayjs): number => {
  const startOfMonth = month.startOf('month');
  const firstWeekday = (startOfMonth.day() + 6) % 7;
  const daysInMonth = startOfMonth.daysInMonth();
  return Math.ceil((firstWeekday + daysInMonth) / 7);
};

const styles = {
  saveButton: {
    borderWidth: 1,
    borderStyle: 'solid',
    width: 320,
    minHeight: 40,
  },
  actionButton: {
    borderWidth: 1,
    borderStyle: 'solid',
    width: 240,
    minHeight: 32,
  },
};
const pickerSize = 40;
const CustomPickersDay = styled(PickersDay, {
  shouldForwardProp: (prop) => prop !== 'selected',
})(({ theme, selected }) => ({
  ...(selected && {
    backgroundColor: 'rgb(4, 207, 192)',
    color: theme.palette.common.white,
    '&:hover, &:focus': {
      backgroundColor: 'rgb(4, 207, 192)',
    },
    borderTopLeftRadius: '50%',
    borderBottomLeftRadius: '50%',
    borderTopRightRadius: '50%',
    borderBottomRightRadius: '50%',
    width: pickerSize,
    height: pickerSize,
  }),
  ...(!selected && {
    color: 'rgb(20, 20, 18)',
    width: pickerSize,
    height: pickerSize,
  }),
}));

type Props = {
  replace?: boolean;
  defaultDateAvailabilityDate: string;
  onClose: () => void;
};

type PickerProps = {
  pickersDayProps: PickersDayProps;
  values: Dayjs[];
  onToggle: (day: Dayjs) => void;
};

const dayLabelMap = strings.general.date as Record<string, string>;

export const CustomCalendarHeader: FunctionComponent<PickerProps> = observer(
  (props) => {
    const { pickersDayProps, values, onToggle } = props;
    if (!values || values.length === 0) {
      return (
        <CustomPickersDay
          {...pickersDayProps}
          disableMargin
          selected={false}
          onClick={() => onToggle(pickersDayProps.day)}
        />
      );
    }
    const selected =
      pickersDayProps.day &&
      values.some((x) => x.isSame(pickersDayProps.day, 'day'));
    return (
      <CustomPickersDay
        {...pickersDayProps}
        disableMargin
        selected={!!selected}
        onClick={() => onToggle(pickersDayProps.day)}
      />
    );
  },
);

export const DaySettingDialog: FunctionComponent<Props> = observer((props) => {
  const { onClose, defaultDateAvailabilityDate, replace } = props;
  const { isLoading } = medicalCareStore;
  const [values, setValues] = React.useState<Dayjs[]>([]);
  const [availability, setAvailability] = React.useState<
    TempHourAvailability[]
  >([]);
  const { doctor, pharmacy } = medicalCareStore;
  const businessHours = useMemo(() => {
    return accountStore.isDoctor
      ? doctor?.businessHours
      : pharmacy?.businessHours;
  }, [accountStore.isDoctor, doctor?.businessHours, pharmacy?.businessHours]);

  useEffect(() => {
    if (defaultDateAvailabilityDate) {
      setValues([
        dayjs(defaultDateAvailabilityDate, 'YYYY-MM-DD').startOf('day'),
      ]);
      const next = getAvailabilityByDate(
        defaultDateAvailabilityDate,
        businessHours,
      );
      setAvailability(next.hours);
    }
  }, [defaultDateAvailabilityDate, businessHours]);

  const onChange = (newValue: dayjs.Dayjs | null) => {
    if (!newValue) return;
    const array = [...values];
    const date = dayjs(newValue).startOf('day');
    const index = array.findIndex((x) => x.isSame(date, 'day'));
    if (index >= 0) {
      array.splice(index, 1);
    } else {
      array.push(date);
    }
    setValues(array);
  };

  const handleUpdateHour = (hourIndex: number, data: TempHourAvailability) => {
    const newHours = JSON.parse(JSON.stringify(availability));
    newHours[hourIndex] = data;
    const overlap = checkForOverlapping(newHours);
    for (let i = 0; i < newHours.length; i++) {
      newHours[i].hasError = false;
    }
    overlap.forEach((index) => {
      newHours[index].hasError = true;
    });
    setAvailability(newHours);
  };

  const handleDeleteHour = (hourIndex: number) => {
    const newHours = JSON.parse(JSON.stringify(availability));
    newHours.splice(hourIndex, 1);
    const overlap = checkForOverlapping(newHours);
    for (let i = 0; i < newHours.length; i++) {
      newHours[i].hasError = false;
    }
    overlap.forEach((index) => {
      newHours[index].hasError = true;
    });
    setAvailability(newHours);
  };

  const handleAddHour = () => {
    const newHours = JSON.parse(JSON.stringify(availability));
    newHours.push({
      from: {
        hour: null,
        minute: null,
      },
      to: {
        hour: null,
        minute: null,
      },
    });
    for (let i = 0; i < newHours.length; i++) {
      newHours[i].hasError = false;
    }
    const overlap = checkForOverlapping(newHours);
    overlap.forEach((index) => {
      newHours[index].hasError = true;
    });
    setAvailability(newHours);
  };

  const isOverlapping = useMemo(() => {
    const overlap = checkForOverlapping(availability);
    if (overlap.length > 0) {
      return true;
    }
    return false;
  }, [availability]);

  const updateWeeklyData = async () => {
    const specialDays: SpecialAvailability[] = getFinalSpecialDays(
      values,
      availability,
      defaultDateAvailabilityDate,
      businessHours,
      replace,
    );
    await medicalCareStore.updateSpecialDay(specialDays);
    onClose();
  };

  const onCancel = () => {
    onClose();
  };

  const pickerValue =
    values.length > 0
      ? values[0]
      : dayjs(defaultDateAvailabilityDate, 'YYYY-MM-DD');
  const [displayMonth, setDisplayMonth] = React.useState<Dayjs>(
    pickerValue.startOf('month'),
  );
  const calendarRowCount = useMemo(() => {
    return getCalendarRowCountByMonth(displayMonth);
  }, [displayMonth]);
  const calendarMinHeight = useMemo(() => {
    return 172 + (calendarRowCount - baseCalendarRowCount) * calendarRowHeight;
  }, [calendarRowCount]);
  const calendarOverlayHeight = useMemo(() => {
    return calendarMinHeight + calendarHeaderHeight - 16;
  }, [calendarMinHeight]);

  const haveAvailability = useMemo(() => {
    return availability.length > 0;
  }, [availability]);

  return (
    <Dialog
      width={720}
      title={strings.hours.hoursSetting}
      titleClassName={'posRelative'}
      open
      handleCancel={onClose}
      isLoading={isLoading}
      footer={<div />}
      keyboard={false}
      closeIcon={
        <CloseCircleOutlined
          style={{
            color: '#9a9a9a',
            fontSize: 32,
          }}
          width={32}
          height={32}
        />
      }
    >
      <Grid2
        container
        display={'flex'}
        flexDirection={'row'}
        alignItems={'flex-start'}
        justifyContent={'center'}
        flexWrap={'nowrap'}
        className="m32b"
      >
        <Grid2
          display={'flex'}
          flexDirection={'column'}
          alignItems={'center'}
          justifyContent={'center'}
          sx={{
            width: 340,
            minWidth: 340,
            maxHeight: maxHeight,
            overflowY: 'auto',
            overflowX: 'hidden',
            padding: 2,
            position: 'relative',
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ja">
            <StaticDatePicker
              autoFocus={false}
              displayStaticWrapperAs="desktop"
              disablePast
              value={pickerValue}
              onMonthChange={setDisplayMonth}
              dayOfWeekFormatter={(date: Dayjs) => {
                const key = `day_of_week_${date.day()}`;
                return dayLabelMap[key] ?? '';
              }}
              slots={{
                day: (slotProps) => (
                  <CustomCalendarHeader
                    pickersDayProps={slotProps}
                    values={values}
                    onToggle={onChange}
                  />
                ),
              }}
              slotProps={{
                day: {
                  disableMargin: true,
                  selected: false,
                  sx: {
                    borderRadius: 0,
                  },
                },
                actionBar: { actions: [] },
              }}
              sx={{
                '.MuiDateCalendar-root': {
                  height: 'auto',
                },
                '.MuiDayCalendar-root': {
                  border: '1px solid #e3e5e9',
                  borderRadius: 4,
                },
                '.MuiPickersSlideTransition-root': {
                  minHeight: calendarMinHeight,
                  height: 'auto',
                },
                '.MuiPickersDay-root': {
                  height: 32,
                },
              }}
            />
          </LocalizationProvider>
          <Grid2
            position="absolute"
            top={72}
            left={0}
            right={0}
            sx={{
              backgroundColor: 'transparent',
              // backgroundColor: 'red',
              // opacity: 0.2,
              width: 340,
              height: calendarOverlayHeight,
            }}
          ></Grid2>
          {haveAvailability && (
            <Button
              disabled={isLoading}
              className={`btn--lightGrey font--18 p16v border--lightGrey flexJustifyCenter flexRow m32t`}
              style={styles.saveButton}
              onClick={handleAddHour}
            >
              <Text
                text={'+ 営業時間を追加'}
                className=" font--16 color--green"
              />
            </Button>
          )}
        </Grid2>
        <Grid2
          container
          display={'flex'}
          flexDirection={'column'}
          justifyContent={haveAvailability ? 'flex-start' : 'center'}
          alignItems={'center'}
          flexWrap={'nowrap'}
          sx={{
            width: 352,
            minWidth: 340,
            height: haveAvailability ? maxHeight : 270,
            overflowY: 'auto',
            paddingTop: !haveAvailability ? 0 : '50px',
          }}
        >
          {!haveAvailability && (
            <Button
              disabled={isLoading}
              className={`btn--lightGrey font--18 p16v border--lightGrey flexJustifyCenter flexRow m32t`}
              style={styles.saveButton}
              onClick={handleAddHour}
            >
              <Text
                text={'+ 営業時間を追加'}
                className=" font--16 color--green"
              />
            </Button>
          )}
          {haveAvailability &&
            availability.map((hour, index) => (
              <Grid2
                key={index}
                sx={{
                  marginBottom: 2,
                  borderRadius: '8px',
                  borderWidth: 1,
                  borderStyle: 'solid',
                  borderColor: '#e0e0e0',
                  padding: '8px 16px',
                  height: 58,
                  display: 'flex',
                  flexWrap: 'nowrap',
                }}
              >
                <TimeSelectRow
                  index={index}
                  from={hour.from}
                  to={hour.to}
                  key={index}
                  handleUpdateHour={handleUpdateHour}
                  onDelete={handleDeleteHour}
                  hasError={hour.hasError}
                />
              </Grid2>
            ))}
        </Grid2>
      </Grid2>
      <Grid2
        display={'flex'}
        flexDirection={'row'}
        alignItems={'center'}
        justifyContent={'center'}
      >
        <Button
          disabled={isLoading}
          className={`btn--secondary fullWidth font--18 fontWeight--bold mr8`}
          style={styles.actionButton}
          onClick={onCancel}
        >
          {strings.action.cancel}
        </Button>
        <Button
          disabled={isLoading || isOverlapping}
          className={`btn--${
            isLoading || isOverlapping ? 'primaryDisableLight' : 'primary'
          } fullWidth font--18 fontWeight--bold ml8`}
          style={styles.actionButton}
          onClick={updateWeeklyData}
        >
          {strings.action.save}
        </Button>
      </Grid2>
    </Dialog>
  );
});
